// This top level component allows us to wrap everything to accommodate loading screens.

import React, { useEffect } from "react";
import { Outlet, useLocation, useNavigate, useNavigation } from "react-router-dom";
import { appInsightsEvents, trackEvent, useAppStore } from "../../common";
import { LoadingScreen } from "./LoadingScreen";
import { ErrorBoundaryView } from "./ErrorBoundary";
import { ErrorBoundary } from "react-error-boundary";
import { SiteMessageBar } from "./SiteMessageBar";
import { WebChatProvider } from "../WebChat/WebChatContext";
import { LazyWebChat } from "../WebChat/LazyWebChat";
import { LazyWebAgent } from "../WebAgent/LazyWebAgent";
import { WebAgentProvider } from "../WebAgent/WebAgentContext";
import { GeneralFeedbackButton } from "./GeneralFeedbackButton";
import PrivacyLink from "./PrivacyLink/PrivacyLink";

export const Root = () => {
  const location = useLocation();
  const navigation = useNavigation();
  const navigate = useNavigate();
  const [appInsights, context, authenticated, orders] = useAppStore((state) => [
    state.appInsights,
    state.context,
    state.authenticated,
    state.orders,
  ]);
  const diningAgentEnabled = useAppStore((state) => state.featureFlags.diningAgentEnabled);

  const { fetch: fetchActiveOrders, values: activeOrders } = orders;
  let interval: string | number | NodeJS.Timeout | undefined;

  useEffect(() => {
    trackEvent(appInsights, appInsightsEvents.Usage.ViewComponent, context, {
      componentName: "Root",
    });

    fetchActiveOrders();
  }, []);

  useEffect(() => {
    interval = setInterval(() => {
      fetchActiveOrders();
    }, 10000);

    if (!activeOrders || !activeOrders.length) {
      clearInterval(interval);
    }

    // cleanup the timer
    return () => clearInterval(interval);
  }, [context, appInsights, activeOrders]);

  useEffect(() => {
    if (authenticated && (location?.pathname === undefined || location?.pathname === "/")) {
      navigate("/buildings");
    }
    if (location?.pathname === "/orders/past") {
      document.title = "Order History - Microsoft Dining";
    } else if (location?.pathname === "/buildings") {
      document.title = "Cafés - Microsoft Dining";
    }
  }, [authenticated, location?.pathname]);

  // Hide Privacy statement on checkout, menu item details, add-to-cart, and station/menu pages
  const isItemPage = /\/item\//.test(location?.pathname || "");
  const isCheckoutPage = /\/checkout/.test(location?.pathname || "");
  const isStationPage = /\/station\//.test(location?.pathname || "");
  const showPrivacy = !isItemPage && !isCheckoutPage && !isStationPage;
  // Hide Provide feedback on add-to-cart / menu item details page
  const showFeedback = !isItemPage;

  return (
    <WebChatProvider>
      <WebAgentProvider>
        <ErrorBoundary FallbackComponent={ErrorBoundaryView}>
          <SiteMessageBar />
          {navigation.state === "loading" ? <LoadingScreen /> : <Outlet />}
          {(showPrivacy || showFeedback) && (
            <div
              data-testid="pageFooter"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "0px 16px 16px 10px",
                maxWidth: "920px",
                marginLeft: "auto",
                marginRight: "auto",
                width: "100%",
                boxSizing: "border-box",
              }}
            >
              {showPrivacy ? <PrivacyLink /> : <span />}
              {showFeedback && <GeneralFeedbackButton />}
            </div>
          )}
          {diningAgentEnabled && (
            <div data-testid="webagent-container">
              <LazyWebAgent />
            </div>
          )}
          {diningAgentEnabled && (
            <div data-testid="webchat-container">
              <LazyWebChat />
            </div>
          )}
        </ErrorBoundary>
      </WebAgentProvider>
    </WebChatProvider>
  );
};
